(() => {
const root = document.querySelector('[data-bfcc-root]');
const query = selector => root?.querySelector(selector);
const form = query('#chain-form');
const results = query('#results');
const fields = {
  stockFront: query('#stock-front'),
  stockRear: query('#stock-rear'),
  stockChain: query('#stock-chain'),
  newFront: query('#new-front'),
  newRear: query('#new-rear'),
  extension: query('#extension'),
  pitch: query('#pitch')
};

function number(field) { return Number.parseFloat(field.value); }
function signed(value) { return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`; }

const oemLookup = {
  make: query('#oem-make'),
  year: query('#oem-year'),
  model: query('#oem-model'),
  status: query('#oem-status'),
  makes: null
};

function setOemOptions(select, placeholder, values = []) {
  if (!select) return;
  select.replaceChildren();
  const blank = document.createElement('option');
  blank.value = '';
  blank.textContent = placeholder;
  select.append(blank);
  values.forEach(value => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = value;
    select.append(option);
  });
  select.disabled = values.length === 0;
}

function preserveScrollDuring(update) {
  const left = window.scrollX;
  const top = window.scrollY;
  update();

  const restore = () => {
    if (Math.abs(window.scrollY - top) > 2) {
      window.scrollTo({ left, top, behavior: 'auto' });
    }
  };

  requestAnimationFrame(() => requestAnimationFrame(restore));
  window.setTimeout(restore, 120);
  window.setTimeout(restore, 300);
}

function resetOemLookup() {
  if (!oemLookup.makes) return;
  setOemOptions(oemLookup.year, 'Choose year');
  setOemOptions(oemLookup.model, 'Choose model');
  oemLookup.status.textContent = 'Choose a make, year and model to load the factory drivetrain setup.';
}

async function loadOemFitment() {
  if (!root?.dataset.oemFitment || !oemLookup.make) return;
  try {
    const response = await fetch(root.dataset.oemFitment, { credentials: 'same-origin', cache: 'force-cache' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    oemLookup.makes = payload.makes || {};
    setOemOptions(oemLookup.make, 'Choose make', Object.keys(oemLookup.makes).sort((a, b) => a.localeCompare(b)));
    resetOemLookup();
  } catch (error) {
    setOemOptions(oemLookup.make, 'OEM lookup unavailable');
    oemLookup.status.textContent = 'OEM lookup is temporarily unavailable. Enter the stock setup manually below.';
  }
}

oemLookup.make?.addEventListener('change', () => {
  preserveScrollDuring(() => {
    const years = Object.keys(oemLookup.makes?.[oemLookup.make.value] || {}).sort((a, b) => Number(b) - Number(a));
    setOemOptions(oemLookup.year, 'Choose year', years);
    setOemOptions(oemLookup.model, 'Choose model');
    oemLookup.status.textContent = years.length ? 'Now choose the model year.' : 'Choose a make, year and model to load the factory drivetrain setup.';
  });
});

oemLookup.year?.addEventListener('change', () => {
  preserveScrollDuring(() => {
    const models = Object.keys(oemLookup.makes?.[oemLookup.make.value]?.[oemLookup.year.value] || {}).sort((a, b) => a.localeCompare(b));
    setOemOptions(oemLookup.model, 'Choose model', models);
    oemLookup.status.textContent = models.length ? 'Now choose the exact model.' : 'Choose a model year to continue.';
  });
});

oemLookup.model?.addEventListener('change', () => {
  const record = oemLookup.makes?.[oemLookup.make.value]?.[oemLookup.year.value]?.[oemLookup.model.value];
  if (!record) return;
  const [pitch, cc, chainLinks, frontTeeth, rearTeeth] = record;
  fields.stockFront.value = frontTeeth;
  fields.stockRear.value = rearTeeth;
  fields.stockChain.value = chainLinks;
  fields.newFront.value = frontTeeth;
  fields.newRear.value = rearTeeth;
  fields.pitch.value = pitch >= 600 ? '0.75' : pitch < 500 ? '0.5' : '0.625';
  const makeReadout = (label, value, className = '') => {
    const item = document.createElement('span');
    item.className = `bfcc__oem-readout ${className}`.trim();
    const key = document.createElement('strong');
    key.textContent = label;
    const data = document.createElement('span');
    data.textContent = value;
    item.append(key, document.createTextNode(' '), data);
    return item;
  };

  const selectedBike = makeReadout(
    'Model Selected:',
    `${oemLookup.year.value} ${oemLookup.model.value}`,
    'bfcc__oem-readout--model'
  );
  const oemSummary = document.createElement('span');
  oemSummary.className = 'bfcc__oem-summary';
  const oemInfoLabel = document.createElement('strong');
  oemInfoLabel.textContent = 'OEM Info:';
  const oemSummaryItems = [
    oemInfoLabel,
    makeReadout('Pitch:', pitch),
    ...(cc ? [makeReadout('CC:', cc)] : []),
    makeReadout('Links:', chainLinks),
    makeReadout('Front:', `${frontTeeth}T`),
    makeReadout('Rear:', `${rearTeeth}T`)
  ];
  oemSummaryItems.forEach((item, index) => {
    if (index) oemSummary.append(document.createTextNode(' '));
    oemSummary.append(item);
  });

  const oemResultLine = document.createElement('span');
  oemResultLine.className = 'bfcc__oem-result-line';
  oemResultLine.append(selectedBike, oemSummary);
  oemLookup.status.replaceChildren(oemResultLine);
  calculate();
});

function renderDrivetrainGraphic() {
  const svg = query('#drivetrain-graphic');
  const sprockets = query('#sprockets');
  const guide = query('#chain-guide');
  const links = query('#chain-links');
  const rollers = query('#chain-rollers');
  if (!svg || !sprockets || !guide || !links || !rollers) return;

  const ns = 'http://www.w3.org/2000/svg';
  const make = (name, attributes = {}) => {
    const element = document.createElementNS(ns, name);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    return element;
  };
  const point = (cx, cy, radius, angle) => [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius];
  const gearOutline = (cx, cy, pitchRadius, teeth, phase = -Math.PI / 2) => {
    const root = pitchRadius - 8;
    const tip = pitchRadius + 5;
    const points = [];
    const toothStep = Math.PI * 2 / teeth;
    for (let index = 0; index < teeth; index += 1) {
      const center = phase + index * toothStep;
      [[-.48, root], [-.22, tip], [.22, tip], [.48, root]].forEach(([offset, radius]) => {
        const [x, y] = point(cx, cy, radius, center + offset * toothStep);
        points.push(`${x.toFixed(2)},${y.toFixed(2)}`);
      });
    }
    return points.join(' ');
  };

  // The chain pins must follow the sprocket pitch circle. Using the chord
  // between tooth valleys (rather than the arc length) keeps every roller
  // centered in a valley as the chain wraps the rear sprocket.
  const rear = { x: 0, y: 215, radius: 135, teeth: 44 };
  const chainPitch = 2 * rear.radius * Math.sin(Math.PI / rear.teeth);
  const front = { x: 150, y: 215, radius: chainPitch / (2 * Math.sin(Math.PI / 15)), teeth: 15 };
  const targetLinks = 74;
  const beltLength = centerDistance => {
    const radiusDifference = rear.radius - front.radius;
    const tangent = Math.sqrt(centerDistance ** 2 - radiusDifference ** 2);
    const alpha = Math.asin(radiusDifference / centerDistance);
    return 2 * tangent + rear.radius * (Math.PI + 2 * alpha) + front.radius * (Math.PI - 2 * alpha);
  };
  let lowDistance = 300;
  let highDistance = 600;
  for (let iteration = 0; iteration < 60; iteration += 1) {
    const candidate = (lowDistance + highDistance) / 2;
    if (beltLength(candidate) < targetLinks * chainPitch) lowDistance = candidate;
    else highDistance = candidate;
  }
  const distance = (lowDistance + highDistance) / 2;
  rear.x = front.x + distance;

  const offsetX = -(rear.radius - front.radius) / distance;
  const offsetY = Math.sqrt(1 - offsetX * offsetX);
  const topFront = { x: front.x + front.radius * offsetX, y: front.y - front.radius * offsetY };
  const topRear = { x: rear.x + rear.radius * offsetX, y: rear.y - rear.radius * offsetY };
  const bottomRear = { x: rear.x + rear.radius * offsetX, y: rear.y + rear.radius * offsetY };
  const bottomFront = { x: front.x + front.radius * offsetX, y: front.y + front.radius * offsetY };

  // The photographed sprocket teeth are taller than the illustrated chain
  // profile. Trim the tips only beneath the wrapped chain, while preserving the
  // complete teeth across the open left side of the sprocket.
  const defs = svg.querySelector('defs');
  const rearClip = make('clipPath', { id: 'rear-sprocket-clip' });
  rearClip.append(make('circle', {
    cx: rear.x,
    cy: rear.y,
    r: rear.radius - 3
  }));
  const exposedRadius = 154;
  const topAngle = Math.atan2(topRear.y - rear.y, topRear.x - rear.x);
  const bottomAngle = Math.atan2(bottomRear.y - rear.y, bottomRear.x - rear.x);
  const exposedTop = {
    x: rear.x + exposedRadius * Math.cos(topAngle),
    y: rear.y + exposedRadius * Math.sin(topAngle)
  };
  const exposedBottom = {
    x: rear.x + exposedRadius * Math.cos(bottomAngle),
    y: rear.y + exposedRadius * Math.sin(bottomAngle)
  };
  rearClip.append(make('path', {
    d: `M ${rear.x} ${rear.y} L ${exposedTop.x} ${exposedTop.y} A ${exposedRadius} ${exposedRadius} 0 0 1 ${exposedBottom.x} ${exposedBottom.y} Z`
  }));
  defs.append(rearClip);

  sprockets.append(make('image', {
    href: root.dataset.rearSprocket,
    x: rear.x - 151,
    y: rear.y - 151,
    width: 302,
    height: 302,
    preserveAspectRatio: 'xMidYMid meet',
    'clip-path': 'url(#rear-sprocket-clip)'
  }));

  const tangentAngle = Math.atan2(topFront.y - front.y, topFront.x - front.x);
  const straightLength = Math.hypot(topRear.x - topFront.x, topRear.y - topFront.y);
  // The product cutout has a tooth tip at 12 o'clock. Offset one half-tooth
  // so the first roller lands in the neighboring valley, not on the tip.
  const rearTargetAngle = -Math.PI / 2 - Math.PI / rear.teeth;
  const rearContactAngle = Math.atan2(topRear.y - rear.y, topRear.x - rear.x);
  const rollerOffset = (straightLength + rear.radius * (rearTargetAngle - rearContactAngle)) % chainPitch;
  const lastFrontRollerAngle = tangentAngle - (chainPitch - rollerOffset) / front.radius;
  const originalFrontGapAngle = -Math.PI / 2 - Math.PI / front.teeth;
  const frontRotation = (lastFrontRollerAngle - originalFrontGapAngle) * 180 / Math.PI;
  const frontOuterRadius = front.radius + 7;
  sprockets.append(make('image', {
    href: root.dataset.frontSprocket,
    x: front.x - frontOuterRadius,
    y: front.y - frontOuterRadius,
    width: frontOuterRadius * 2,
    height: frontOuterRadius * 2,
    preserveAspectRatio: 'xMidYMid meet',
    transform: `rotate(${frontRotation} ${front.x} ${front.y})`
  }));

  guide.setAttribute('d', [
    `M ${topFront.x} ${topFront.y}`,
    `L ${topRear.x} ${topRear.y}`,
    `A ${rear.radius} ${rear.radius} 0 1 1 ${bottomRear.x} ${bottomRear.y}`,
    `L ${bottomFront.x} ${bottomFront.y}`,
    `A ${front.radius} ${front.radius} 0 0 1 ${topFront.x} ${topFront.y}`,
    'Z'
  ].join(' '));
  guide.setAttribute('stroke', 'none');

  const total = guide.getTotalLength();
  const count = targetLinks;
  const spacing = total / count;
  const at = length => guide.getPointAtLength((length + total) % total);

  const rollerPoints = Array.from({ length: count }, (_, index) => {
    const position = rollerOffset + index * spacing;
    const center = at(position);
    return { x: center.x, y: center.y };
  });

  const plate = (length, outer) => {
    const half = length / 2;
    const end = outer ? 7.2 : 6.1;
    const waist = outer ? 4.5 : 3.8;
    const shoulder = Math.min(6.2, length * .31);
    return [
      `M ${-half} ${-end}`,
      `C ${-half + shoulder} ${-end} ${-half + shoulder} ${-waist} 0 ${-waist}`,
      `C ${half - shoulder} ${-waist} ${half - shoulder} ${-end} ${half} ${-end}`,
      `A ${end} ${end} 0 0 1 ${half} ${end}`,
      `C ${half - shoulder} ${end} ${half - shoulder} ${waist} 0 ${waist}`,
      `C ${-half + shoulder} ${waist} ${-half + shoulder} ${end} ${-half} ${end}`,
      `A ${end} ${end} 0 0 1 ${-half} ${-end}`,
      'Z'
    ].join(' ');
  };

  // A roller chain alternates narrower inner plates and wider outer plates.
  // Build the inner row first, then the outer row, so adjacent links share one
  // pin instead of looking like duplicated stickers laid around a circle.
  [false, true].forEach(outer => {
    for (let index = outer ? 0 : 1; index < count; index += 2) {
      const start = rollerPoints[index];
      const end = rollerPoints[(index + 1) % count];
      const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
      const segmentLength = Math.hypot(end.x - start.x, end.y - start.y);
      const angle = Math.atan2(end.y - start.y, end.x - start.x) * 180 / Math.PI;
      const group = make('g', { transform: `translate(${center.x} ${center.y}) rotate(${angle})` });
      group.append(make('path', {
        d: plate(segmentLength, outer),
        fill: outer ? 'url(#chain-gold)' : '#6f5521',
        stroke: outer ? '#6b501b' : '#3d321d',
        'stroke-width': outer ? '.9' : '.75'
      }));
      group.append(make('path', {
        d: `M ${-segmentLength / 2 + 5.2} -1.45 L ${segmentLength / 2 - 5.2} -1.45`,
        stroke: outer ? '#ffe7a0' : '#ad8840',
        'stroke-width': '.85',
        'stroke-linecap': 'round',
        opacity: outer ? '.72' : '.5'
      }));
      links.append(group);
    }
  });

  // Visible rivet heads make the engagement unambiguous: each one is the
  // roller center seated between two rear-sprocket teeth.
  rollerPoints.forEach(({ x, y }) => {
    rollers.append(make('circle', {
      cx: x,
      cy: y,
      r: '4.4',
      fill: 'url(#chain-steel)',
      stroke: '#4d4536',
      'stroke-width': '.9'
    }));
    rollers.append(make('circle', {
      cx: x,
      cy: y,
      r: '2.55',
      fill: '#d8bd70',
      stroke: '#70551d',
      'stroke-width': '.55'
    }));
    rollers.append(make('circle', {
      cx: x - .55,
      cy: y - .7,
      r: '.8',
      fill: '#fff1bc',
      opacity: '.8'
    }));
  });
}

function calculate({ scroll = false, showErrors = false } = {}) {
  const values = Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, number(field)]));
  const required = ['stockFront', 'stockRear', 'newFront', 'newRear'];
  const invalid = required.some(key => !Number.isFinite(values[key]) || values[key] <= 0) || !Number.isFinite(values.pitch) || values.pitch <= 0;
  const stockChainMissing = !Number.isFinite(values.stockChain) || values.stockChain <= 0 || values.stockChain % 2 !== 0;
  const error = query('#form-error');

  if (invalid) {
    error.textContent = showErrors ? 'Enter the stock front and rear sprocket tooth counts.' : '';
    query('.bfcc__link-count').hidden = true;
    query('#chain-result-label').textContent = 'STOCK SETUP REQUIRED';
    query('#length-note').textContent = 'Enter the stock front and rear sprocket tooth counts to compare setups.';
    query('.bfcc__ratio-table').hidden = true;
    query('.bfcc__ride-feel').hidden = true;
    query('#warning').hidden = true;
    results.hidden = false;
    return false;
  }

  error.textContent = showErrors && stockChainMissing ? 'Enter the stock chain length as an even number of links.' : '';
  const extension = Number.isFinite(values.extension) ? Math.max(0, values.extension) : 0;
  let recommended = null;
  if (!stockChainMissing) {
    const sprocketAdjustment = ((values.newFront + values.newRear) - (values.stockFront + values.stockRear)) * 0.5;
    const extensionAdjustment = (extension * 2) / values.pitch;
    const estimate = values.stockChain + sprocketAdjustment + extensionAdjustment;
    recommended = Math.ceil(estimate / 2) * 2;
  }
  const stockRatio = values.stockRear / values.stockFront;
  const newRatio = values.newRear / values.newFront;
  const ratioChange = ((newRatio - stockRatio) / stockRatio) * 100;

  let feel = 'VERY CLOSE TO STOCK GEARING.';
  if (ratioChange > 2) feel = 'MORE ACCELERATION. LOWER TOP SPEED.';
  if (ratioChange < -2) feel = 'MORE TOP SPEED. SOFTER ACCELERATION.';

  const linkCount = query('.bfcc__link-count');
  query('.bfcc__ratio-table').hidden = false;
  query('.bfcc__ride-feel').hidden = false;
  query('#recommended').textContent = recommended ?? '';
  linkCount.hidden = !recommended;
  query('#chain-result-label').textContent = recommended ? 'RECOMMENDED CHAIN' : 'STOCK CHAIN LENGTH REQUIRED';
  query('#stock-ratio').textContent = stockRatio.toFixed(3);
  query('#new-ratio').textContent = newRatio.toFixed(3);
  query('#ratio-change').textContent = signed(ratioChange);
  query('#setup-result').textContent = feel;
  query('#length-note').textContent = recommended
    ? `Start with ${recommended} links and trim to final fit during installation.`
    : 'Enter the stock chain length above to calculate the recommended link count.';
  query('#warning').hidden = Math.abs(ratioChange) <= 10;
  results.hidden = false;
  if (scroll) results.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return !stockChainMissing;
}

form?.addEventListener('submit', event => {
  event.preventDefault();
  calculate({ scroll: true, showErrors: true });
});

Object.values(fields).forEach(field => {
  field?.addEventListener('input', () => calculate());
  field?.addEventListener('change', () => calculate());
});

const ratioChartModal = query('#ratio-chart-modal');
const ratioChartOpen = query('[data-ratio-chart-open]');
const ratioChartClose = query('[data-ratio-chart-close]');
const ratioChartSave = query('[data-ratio-chart-save]');
let ratioChartFilePromise;

function getRatioChartFile() {
  if (!ratioChartFilePromise) {
    ratioChartFilePromise = fetch(ratioChartSave.href)
      .then(response => {
        if (!response.ok) throw new Error('Unable to load gear ratio chart.');
        return response.blob();
      })
      .then(blob => new File([blob], 'Braap-Factory-Gear-Ratio-Chart.png', { type: blob.type || 'image/png' }));
  }
  return ratioChartFilePromise;
}

function downloadRatioChart() {
  const link = document.createElement('a');
  link.href = ratioChartSave.href;
  link.download = 'Braap-Factory-Gear-Ratio-Chart.png';
  document.body.append(link);
  link.click();
  link.remove();
}

async function saveRatioChart(event) {
  event.preventDefault();
  try {
    const file = await getRatioChartFile();
    if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
      await navigator.share({ files: [file], title: 'Braap Factory Gear Ratio Chart' });
      return;
    }
  } catch (error) {
    if (error?.name === 'AbortError') return;
  }
  downloadRatioChart();
}

function openRatioChart(event) {
  event?.preventDefault();
  ratioChartModal.hidden = false;
  document.body.style.overflow = 'hidden';
  ratioChartClose.focus();
}

function closeRatioChart() {
  ratioChartModal.hidden = true;
  document.body.style.overflow = '';
  ratioChartOpen.focus();
}

ratioChartOpen?.addEventListener('click', openRatioChart);
ratioChartClose?.addEventListener('click', closeRatioChart);
ratioChartSave?.addEventListener('click', saveRatioChart);
ratioChartModal?.addEventListener('click', event => {
  if (event.target === ratioChartModal) closeRatioChart();
});
document.addEventListener('keydown', event => {
  if (event.key === 'Escape' && ratioChartModal && !ratioChartModal.hidden) closeRatioChart();
});

form?.addEventListener('reset', () => {
  window.setTimeout(() => {
    resetOemLookup();
    calculate();
  }, 0);
});

if (root && form) {
  renderDrivetrainGraphic();
  loadOemFitment();
  if (ratioChartSave) getRatioChartFile().catch(() => {});
  calculate();
}
})();
